import './CreateDialogSortingMap.scss';

import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { useEffect, useMemo } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import useToastMessage from '../../../../../../hooks/useToastMessage';
import { WithData } from '../../../../../../types/api';
import { EntityIdRouteParams } from '../../../../../../types/routing';
import { sequential } from '../../../../../../utils/helpers/functions';
import FaviconLoader from '../../../../../Favicon/FaviconLoader';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';
import { SortingMachinesType } from '../../../SortingMachine.functions';
import {
  SortingMapsPortsType,
  SortingMapsType,
} from '../../SortingMaps.functions';
import {
  CreateSortingMapsType,
  getDefaultValues,
  getValidationSchema,
  toApiData,
  toApiDataPorts,
} from './CreateDialogSortingMap.functions';
import PortRow from './PortRow';

type Props = {
  isShown: boolean;
  data: SortingMapsType | undefined;
  sortingMachine: SortingMachinesType | undefined;
  isEditDialog: boolean;
  onHide: () => void;
  reloadCollection: () => void;
};

function CreateDialogSortingMap({
  isShown,
  data,
  sortingMachine,
  isEditDialog,
  onHide,
  reloadCollection,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const isOnMobile = useMediaQuery('(max-width: 768px)');
  const maxPorts =
    sortingMachine && sortingMachine?.maxPorts
      ? sortingMachine?.maxPorts - 2
      : 100;

  const pointTypes = useMemo(
    () => [
      { value: 1, label: t('Warehouse') },
      { value: 2, label: t('Drop point') },
      { value: 3, label: t('Locker') },
    ],
    [t]
  );

  const { data: portsData, isLoading } = useAxiosHook<
    WithData<SortingMapsPortsType[]>
  >(`/orders/sorting/machine/${id}/map/${data?.id}/port`, {
    skipWhen: !isShown && !data?.id,
  });

  const defaultValues = useMemo(
    () => getDefaultValues(isEditDialog, data, portsData?.data),
    [isEditDialog, data, portsData?.data]
  );

  const validationSchema = useMemo(
    () => getValidationSchema(t, sortingMachine),
    [t, sortingMachine]
  );

  const methods = useForm<CreateSortingMapsType>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  const { control, handleSubmit, reset, getValues, watch } = methods;
  console.log('values ', getValues());

  const {
    data: createData,
    error: createError,
    isLoading: isCreateLoading,
    reload: createReload,
  } = useAxiosHook(
    {
      url: `/orders/sorting/machine/${id}/map`,
      method: 'POST',
    },
    {
      skipWhen: true,
    }
  );

  const { reload: reloadPorts } = useAxiosHook<any>(
    {
      url: '',
      method: 'POST',
    },
    {
      skipWhen: true,
    }
  );

  const {
    data: editData,
    error: editError,
    isLoading: isEditLoading,
    reload: editReload,
  } = useAxiosHook<any>(
    {
      url: `/orders/sorting/machine/${id}/map/${data?.id}`,
      method: 'PUT',
    },
    {
      skipWhen: true,
    }
  );

  useToastMessage(createData as any, createError, {
    success: {
      summary: t('The sorting map has been created successfully.'),
      callback: () => {
        const idMap = (createData as any)?.id ?? 0;
        if (getValues()?.ports?.length > 1) {
          const ports = toApiDataPorts(getValues()?.ports);
          reloadPorts({
            url: `/orders/sorting/machine/${id}/map/${idMap}/port`,
            data: ports,
          });
        }
        sequential(onHide, reloadCollection);
      },
    },
    error: {
      summary: t('An error occurred while creating the sorting map.'),
      callback: () => {
        console.error('createError ', createError);
        if (createError?.response?.status === 409) {
          return t('The sorting map already exists.');
        }
        return t('An error occurred while creating the sorting map.');
      },
    },
  });

  useToastMessage(editData, editError, {
    success: {
      summary: t('The sorting map has been updated successfully.'),
      callback: () => {
        const idMap = (editData as any)?.id ?? 0;
        if (getValues()?.ports?.length > 1) {
          const ports = toApiDataPorts(getValues()?.ports);
          reloadPorts({
            url: `/orders/sorting/machine/${id}/map/${idMap}/port`,
            data: ports,
          });
        }
        sequential(onHide, reloadCollection);
      },
    },
    error: {
      summary: t('An error occurred while updating the sorting map.'),
      callback: () => {
        console.error('editError ', editError);
        if (editError?.response?.status === 409) {
          return t('The sorting map already exists.');
        }
        return t('An error occurred while updating the sorting map.');
      },
    },
  });

  const {
    fields: portsFields,
    append: appendPortsFields,
    remove: removePortsFields,
  } = useFieldArray({
    control,
    name: 'ports',
  });

  const dialogFooter = (
    <>
      <Button
        type="button"
        label={t('Cancel')}
        className="p-button-text"
        onClick={() => onHide()}
      />
      <Button
        type="submit"
        form="sorting-map-create-edit-form"
        label={
          isEditLoading || isCreateLoading
            ? t('Saving...')
            : isEditDialog
            ? t('Save changes')
            : t('Add sorting map')
        }
        disabled={isEditLoading || isCreateLoading}
      />
    </>
  );

  function handleCreation(data: CreateSortingMapsType) {
    const params = toApiData(data);
    createReload({ data: params });
  }

  function handleEditing(data: CreateSortingMapsType) {
    const params = toApiData(data);
    editReload({ data: params });
  }

  function handleFormSubmission(values: CreateSortingMapsType) {
    if (isEditDialog) {
      handleEditing(values);
      return;
    }

    handleCreation(values);
  }

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Bounds distinct port numbers, not rows: small hubs are grouped several-to-a-port, so rows
  // legitimately outnumber the machine's ports.
  const usedPortCount = new Set(
    (watch('ports') || []).map((p) => Number(p.port))
  ).size;
  const canAddPort = usedPortCount < maxPorts;

  function addPorts() {
    const reservedPorts = new Set([watch('errorPort'), watch('unmatchedPort')]);
    const existingPorts = new Set(
      (getValues()?.ports || []).map((p) => Number(p.port))
    );

    if (existingPorts.size >= maxPorts) {
      return;
    }

    let nextPort = 1;
    while (existingPorts.has(nextPort) || reservedPorts.has(nextPort)) {
      nextPort++;
    }

    appendPortsFields({
      pointTypeId: 1,
      pointName: '',
      logisticsPointId: undefined,
      port: nextPort,
    });
  }

  // Groups another point onto an existing port, which is what makes a shared port. Adds no new port
  // number, so it stays available once every port of the machine is in use.
  function addPointToPort(port: number) {
    appendPortsFields({
      pointTypeId: 1,
      pointName: '',
      logisticsPointId: undefined,
      port,
    });
  }

  return (
    <Dialog
      header={isEditDialog ? t('Edit sorting map') : t('Create sorting map')}
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 680, maxWidth: '100%' }}
      className="create-sorting-maps-page-dialog"
    >
      <FormProvider {...methods}>
        <form
          id="sorting-map-create-edit-form"
          onSubmit={handleSubmit(handleFormSubmission)}
        >
          <div className="p-fluid">
            <FieldWithErrors name="name" label={t('Name')}>
              <Controller
                name="name"
                render={({ field }) => (
                  <InputText
                    id="name"
                    name="name"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="description" label={t('Description')}>
              <Controller
                name="description"
                render={({ field }) => (
                  <InputText
                    id="description"
                    name="description"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="errorPort" label={t('Error Port')}>
              <Controller
                name="errorPort"
                render={({ field }) => (
                  <InputText
                    id="errorPort"
                    name="errorPort"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="unmatchedPort" label={t('Unmatched Port')}>
              <Controller
                name="unmatchedPort"
                render={({ field }) => (
                  <InputText
                    id="unmatchedPort"
                    name="unmatchedPort"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FieldWithErrors>

            <FieldWithErrors name="ports" label={false}>
              <span className="section-title">{t('Ports')}</span>
              <table cellPadding="0" cellSpacing="0">
                {portsFields?.length > 0 && (
                  <thead>
                    <tr>
                      <th>{t('Port')}</th>
                      <th>{t('Point Type')}</th>
                      <th>{t('Points')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                )}
                {isLoading ? (
                  <FaviconLoader />
                ) : (
                  <tbody className="ports-fields">
                    {portsFields?.map((port, index) => (
                      <PortRow
                        key={port.id}
                        index={index}
                        pointTypes={pointTypes}
                        removePortsFields={removePortsFields}
                        addPointToPort={addPointToPort}
                      />
                    ))}
                  </tbody>
                )}
              </table>
              <div className="ports-buttons">
                <Button
                  type="button"
                  icon="fas fa-plus"
                  className="p-button-outlined add-field"
                  onClick={addPorts}
                  disabled={!canAddPort}
                >
                  {canAddPort ? t('Add port') : 'Max ports'}
                </Button>
              </div>
            </FieldWithErrors>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default CreateDialogSortingMap;
